import React, { useEffect, useCallback } from 'react';
import { useSelector, connect, useDispatch } from 'react-redux';
import lodash from 'lodash';
import { useHistory, useLocation, RouteComponentProps } from 'react-router-dom';
import {
  APP_URL,
  CFCProps,
  AUTH_ERR_MSG,
  USERSROLE,
  SCREEN_SIZE,
  AUTH_TYPE,
  DBYD_1100_URL
} from 'types';
import {
  selectIsAuthenticated,
  selectScreenSize,
  currentUser,
  selectRedirectPath
} from 'redux/session/selectors';
import { selectAppVisiblePanel } from 'redux/app/selectors';
import { selectOTPConfig } from 'redux/auth/selectors';
import { RootState } from 'redux/root.reducer';
import Services from 'services';
import { GenericPageLayout } from 'layouts/genericPage.layout';
import { MachineLearning_03 as MachineLearning } from '@carbon/pictograms-react';
import { Button, Link } from 'carbon-components-react';
import { setAppLoginType, setAppVisiblePanel } from 'redux/app/actions';
import { fetchAuthRequest } from 'redux/auth/action';
import { contactSupportFlag } from 'redux/user/actions';
import { fetchEnquiryHistory } from 'redux/enquiries';
import Utils from 'utilities/helper.util';
import {
  redirectResidentialEnquirerIfNeeded,
  toLocationShape,
  toPendingPath
} from 'utilities/residential-redirect.util';

export const Login: React.FC<Props & RouteComponentProps<{}, any, any>> = (
  props: Props
) => {
  const { login, app, enquiresCache } = props;
  const { isTermsAccepted, error, isOTPActivated }: any = login;
  const { userRole } = app;
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation<{ from?: Location }>();
  const isAuthenticated: boolean = useSelector(selectIsAuthenticated);
  const user: any = useSelector(currentUser);
  const redirectPath: string | undefined = useSelector(selectRedirectPath);
  const appVisiblePanel: string = useSelector(selectAppVisiblePanel);
  const OTPConfig: any = useSelector(selectOTPConfig);
  const screenSize = useSelector(selectScreenSize);
  const _: any = lodash;

  const intendedPath =
    toPendingPath(location?.state?.from) || redirectPath || undefined;

  const handleSSO = useCallback(() => {
    dispatch(setAppLoginType(AUTH_TYPE.SSO_LOGIN));
    dispatch(
      fetchAuthRequest({
        context: AUTH_TYPE.SSO_LOGIN,
        redirectTo: intendedPath
      })
    );
  }, [dispatch, intendedPath]);

  const handleOTP = () => {
    dispatch(setAppLoginType(AUTH_TYPE.OPT_LOGIN));
    history.push({
      pathname: APP_URL.ONE_TIME_PASSCODE,
      state: {
        from: location.state?.from || toLocationShape(redirectPath)
      }
    });
  };

  const handleSignUp = () => {
    Services.auth.signupWithSSOService();
  };

  useEffect(() => {
    const queryParams = {
      fields: 'id%2CcreatedAt%2CexternalId%2CuserReference',
      include: 'Address',
      orderBy: '-createdAt',
      limit: 1,
      offset: 0,
      filter: `userId:${user.id}`
    };
    user.id && dispatch(fetchEnquiryHistory(queryParams));
  }, [dispatch, user.id]);

  useEffect(() => {
    if (
      isAuthenticated &&
      !Utils.isObjectEmpty(enquiresCache) &&
      enquiresCache.data.enquiries
    ) {
      const showTerms =
        !isTermsAccepted && error?.message === AUTH_ERR_MSG.USER_ACCEPT_TERMS;
      const showDashboradPage =
        isAuthenticated &&
        userRole !== USERSROLE.OTHER &&
        enquiresCache.data.enquiries.items?.length > 0;

      if (redirectResidentialEnquirerIfNeeded(userRole, user, intendedPath)) {
        return;
      }
      if (showDashboradPage) {
        history.push(APP_URL.DASHBOARD);
      } else if (showTerms) {
        history.push(APP_URL.AUTH_USER_TERMS);
      } else {
        history.push(APP_URL.LODGE_NEW_ENQUIRY);
      }
    }
  }, [
    history,
    isTermsAccepted,
    error,
    isAuthenticated,
    userRole,
    user,
    enquiresCache,
    intendedPath
  ]);

  useEffect(() => {
    if (appVisiblePanel === 'support') {
      dispatch(setAppVisiblePanel(''));
      dispatch(contactSupportFlag(true));
    }
  }, [dispatch, appVisiblePanel]);

  return (
    <div id="login" className="auth" data-testid="login">
      <GenericPageLayout
        pageClassName="auth--wrapper"
        isOTPActivated={isOTPActivated}
        showOTPNotification={true}
        bodyFooterIcon={
          screenSize !== SCREEN_SIZE.SM && (
            <MachineLearning className="brain--icon" />
          )
        }
      >
        {userRole === USERSROLE.OTHER && !_.isUndefined(OTPConfig) && (
          <>
            <h1 className="auth--heading">Welcome to BYDA</h1>
            <div className="auth--buttons">
              <Button
                kind="tertiary"
                className="auth--btn-sign-up"
                onClick={() => handleSignUp()}
                disabled={isOTPActivated}
              >
                Sign up
              </Button>
              <Button
                kind="primary"
                className="auth--btn-sign-in"
                onClick={isOTPActivated ? handleOTP : handleSSO}
              >
                {isOTPActivated ? 'Get temporary passcode' : 'Sign in'}
              </Button>
            </div>
            <div
              className={
                isOTPActivated
                  ? 'auth--other-links-disabled'
                  : 'auth--other-links'
              }
            >
              {/* <div>
                <Link
                  onClick={() => Services.auth.forgotSSOPassword()}
                  disabled={isOTPActivated}
                >
                  Forgot password
                </Link>
              </div> */}
              <div>
                <Link onClick={handleOTP}>
                  Sign in with a temporary passcode
                </Link>
              </div>
              <div>
                <Link href={DBYD_1100_URL.LOGIN_HELP} target="_blank">
                  Help with signing in and signing up
                </Link>
              </div>
            </div>
          </>
        )}
      </GenericPageLayout>
    </div>
  );
};

const mapStateToProps = ({ app, login, enquiresCache }: RootState) => ({
  app,
  login,
  enquiresCache
});

const connector = connect(mapStateToProps);
type Props = CFCProps<typeof connector>;
Login.displayName = 'Login';

export default connector(Login);
